import React, { forwardRef } from 'react';
import './ReusableButton.css';

const SOLID_COLORS = new Set([
  'brand',
  'secondary',
  'tertiary',
  'success',
  'danger',
  'warning',
  'dark',
  'ghost',
]);

const OUTLINE_COLORS = new Set(['brand', 'gray', 'success', 'danger', 'warning']);

/**
 * @typedef {'xs' | 'sm' | 'md' | 'lg' | 'xl'} ButtonSize
 * @typedef {'solid' | 'outline'} ButtonVariant
 * @typedef {'default' | 'pill'} ButtonShape
 */

function classNames(...parts) {
  return parts.filter(Boolean).join(' ');
}

/**
 * Shared button: solid (filled) and outline styles, optional pill shape, five sizes, optional leading/trailing icons.
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children]
 * @param {'solid' | 'outline'} [props.variant='solid']
 * @param {'default' | 'pill'} [props.shape='default'] — `pill` = fully rounded ends
 * @param {ButtonSize} [props.size='sm']
 * @param {string} [props.color] — solid: brand | secondary | tertiary | success | danger | warning | dark | ghost. outline: brand | gray | success | danger | warning
 * @param {React.ReactNode} [props.iconStart] — icon before label
 * @param {React.ReactNode} [props.iconEnd] — icon after label
 * @param {boolean} [props.fullWidth]
 * @param {string} [props.className]
 * @param {boolean} [props.disabled]
 * @param {'button' | 'submit' | 'reset'} [props.type='button']
 */
const ReusableButton = forwardRef(function ReusableButton(
  {
    children,
    variant = 'solid',
    shape = 'default',
    size = 'md',
    color,
    iconStart,
    iconEnd,
    fullWidth = false,
    className,
    disabled = false,
    type = 'button',
    ...rest
  },
  ref
) {
  const resolvedColor = color || 'brand';

  let toneClass = `rb--${resolvedColor}`;
  if (variant === 'solid' && !SOLID_COLORS.has(resolvedColor)) {
    toneClass = 'rb--brand';
  }
  if (variant === 'outline' && !OUTLINE_COLORS.has(resolvedColor)) {
    toneClass = 'rb--brand';
  }

  const hasLabel = children != null && children !== '';

  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={classNames(
        'rb-btn',
        variant === 'solid' && 'rb-btn--solid',
        variant === 'outline' && 'rb-btn--outline',
        shape === 'pill' && 'rb-btn--pill',
        `rb-btn--${size}`,
        toneClass,
        fullWidth && 'rb-btn--full',
        className
      )}
      {...rest}
    >
      {iconStart ? (
        <span className="rb-btn__icon rb-btn__icon--start" aria-hidden={hasLabel ? true : undefined}>
          {iconStart}
        </span>
      ) : null}
      {hasLabel ? <span className="rb-btn__label">{children}</span> : null}
      {iconEnd ? (
        <span className="rb-btn__icon rb-btn__icon--end" aria-hidden={hasLabel ? true : undefined}>
          {iconEnd}
        </span>
      ) : null}
    </button>
  );
});

ReusableButton.displayName = 'ReusableButton';

export default ReusableButton;
